import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { X, ChevronRight, User, MapPin, Clock, AlertCircle, Check, Loader } from 'lucide-react';
import { auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { getBusSeatLayout, getBusPromoCodes, createBusOrder, openBusPaymentModal, updateBusBookingStatus } from '../services/busBookingAPI';
import BusPromoCodeList from '../components/bookingBus/BusPromoCodeList';
import './BusBookingPage.css';
import { FEATURES } from '../config/features';

// Strip invisible Unicode control/format chars from city names (LRM, ZWS, BOM etc.)
const cleanCity = (s) => s ? s.replace(/[​-‏‪-‮⁠-⁤﻿­]/g, '').trim() : s;

// ─── Layout API via RC orchestrator ──────────────────────────────────

// ─── Seat grid helpers ────────────────────────────────────────────────
function buildGrid(seats) {
    const maxRow = Math.max(...seats.map(s => s.row + (s.length - 1)));
    const maxCol = Math.max(...seats.map(s => s.column + (s.width - 1)));
    return { maxRow, maxCol };
}

function SeatCell({ seat, isSelected, onToggle }) {
    if (!seat) return <div className="bb-seat-empty" />;

    const cls = [
        'bb-seat',
        seat.sleeper ? 'bb-seat--sleeper' : 'bb-seat--seater',
        !seat.available ? 'bb-seat--booked' : '',
        seat.ladiesSeat ? 'bb-seat--ladies' : '',
        seat.malesSeat  ? 'bb-seat--males'  : '',
        isSelected      ? 'bb-seat--selected' : '',
    ].filter(Boolean).join(' ');

    const label     = seat.ladiesSeat ? '♀' : seat.malesSeat ? '♂' : '';
    const fareLabel = seat.available
        ? `₹${seat.fare.toLocaleString('en-IN')}`
        : (seat.bookedBy || 'Booked');

    return (
        <button
            className={cls}
            disabled={!seat.available}
            onClick={() => seat.available && onToggle(seat)}
            title={seat.id}
        >
            <span className="bb-seat-icon">
                {isSelected ? <Check size={10} strokeWidth={3} /> : label}
            </span>
            <span className="bb-seat-fare">{fareLabel}</span>
        </button>
    );
}

function DeckMap({ seats, selectedIds, onToggle }) {
    if (!seats.length) return null;
    const { maxRow, maxCol } = buildGrid(seats);

    const lookup = {};
    seats.forEach(s => { lookup[`${s.row}-${s.column}`] = s; });

    const rows = [];
    for (let r = 0; r <= maxRow; r++) {
        const cols = [];
        for (let c = 0; c <= maxCol; c++) {
            const seat = lookup[`${r}-${c}`];
            const isSubCell = !seat && seats.some(s =>
                s.row <= r && r < s.row + s.length &&
                s.column <= c && c < s.column + s.width &&
                !(s.row === r && s.column === c)
            );
            if (isSubCell) continue;

            cols.push(
                <SeatCell
                    key={`${r}-${c}`}
                    seat={seat}
                    isSelected={seat ? selectedIds.has(seat.id) : false}
                    onToggle={onToggle}
                />
            );
        }
        rows.push(
            <div key={r} className="bb-seat-row">{cols}</div>
        );
    }

    return (
        <div className="bb-deck-map">
            <div className="bb-deck-front">
                <div className="bb-steering">
                    <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M12 3v6M12 15v6M3 12h6M15 12h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                </div>
            </div>
            <div className="bb-seats-grid">{rows}</div>
        </div>
    );
}

// ─── Info panel sub-components ────────────────────────────────────────
function CancellationTable({ policies }) {
    if (!policies?.length) {
        return <p className="bb-info-empty">Cancellation policy not available.</p>;
    }
    return (
        <div className="bb-cancel-table">
            <div className="bb-cancel-table-head">
                <span>Time before travel</span>
                <span>Refund</span>
            </div>
            {policies.map((p, i) => (
                <div
                    key={i}
                    className={`bb-cancel-table-row ${i === policies.length - 1 ? 'bb-cancel-table-row--last' : ''}`}
                >
                    <span>{p.cutoffTime}</span>
                    <span className="bb-cancel-refund">{p.refundInPercentage}%</span>
                </div>
            ))}
        </div>
    );
}

function PointList({ points, dateKey }) {
    if (!points?.length) return <p className="bb-info-empty">No points available.</p>;
    return (
        <div className="bb-point-list">
            {points.map(pt => (
                <div key={pt.id} className="bb-point-row">
                    <div className="bb-point-time">
                        <span>{pt.time?.replace(':00 ', ' ')}</span>
                        {pt[dateKey] && (
                            <span className="bb-point-date">
                                {new Date(pt[dateKey]).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                            </span>
                        )}
                    </div>
                    <div className="bb-point-detail">
                        <div className="bb-point-name">
                            {pt.pointName?.replace(/^cityflo:\s+/, '').trim()}
                        </div>
                        {(pt.landmark || pt.address) && (
                            <div className="bb-point-addr">{pt.landmark || pt.address}</div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

// ─── Step 1: Seat Selection ───────────────────────────────────────────
function StepSeats({ layoutData, busInfo, selectedSeats, onToggleSeat, onNext }) {
    const [deck, setDeck]       = useState('lower');
    const [infoTab, setInfoTab] = useState('info');

    const lowerSeats = (layoutData?.seats || []).filter(s => s.zIndex === 0);
    const upperSeats = (layoutData?.seats || []).filter(s => s.zIndex === 1);
    const hasUpper   = upperSeats.length > 0;

    const selectedIds  = new Set(selectedSeats.map(s => s.id));
    const totalFare    = selectedSeats.reduce((sum, s) => sum + s.fare, 0);
    const hasSelection = selectedSeats.length > 0;

    const policies       = busInfo?.cancellation_policies || [];
    const boardingPoints = layoutData?.boardingPoints || [];
    const droppingPoints = layoutData?.droppingPoints || [];
    const amenityList    = Array.isArray(busInfo?.busAmenities || busInfo?.amenities)
        ? (busInfo.busAmenities || busInfo.amenities)
        : [];

    const legend = [
        { cls: 'bb-legend-avail',    label: 'Available' },
        { cls: 'bb-legend-selected', label: 'Selected' },
        { cls: 'bb-legend-booked',   label: 'Booked' },
        { cls: 'bb-legend-ladies',   label: 'Ladies' },
    ];

    return (
        <div className="bb-step-seats">
            {/* Left: seat map */}
            <div className="bb-seat-panel">
                {hasUpper ? (
                    <div className="bb-deck-tabs">
                        <button
                            className={`bb-deck-tab ${deck === 'lower' ? 'active' : ''}`}
                            onClick={() => setDeck('lower')}
                        >Lower deck</button>
                        <button
                            className={`bb-deck-tab ${deck === 'upper' ? 'active' : ''}`}
                            onClick={() => setDeck('upper')}
                        >Upper deck</button>
                    </div>
                ) : (
                    <div className="bb-deck-label">Lower deck</div>
                )}

                <DeckMap
                    seats={deck === 'lower' ? lowerSeats : upperSeats}
                    selectedIds={selectedIds}
                    onToggle={onToggleSeat}
                />

                <div className="bb-legend">
                    {legend.map(l => (
                        <div key={l.label} className="bb-legend-item">
                            <div className={`bb-legend-swatch ${l.cls}`} />
                            <span>{l.label}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Right: bus info panel */}
            <div className="bb-info-panel">
                <div className="bb-info-header">
                    <div className="bb-info-operator">
                        {busInfo?.travel_name || busInfo?.operatorName || 'Bus Service'}
                    </div>
                    <div className="bb-info-bustype">{busInfo?.bus_type || busInfo?.busType || ''}</div>
                    {busInfo?.origin_dep_dt && (
                        <div className="bb-info-timing">
                            {new Date(busInfo.origin_dep_dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            {' → '}
                            {busInfo.dest_arr_dt && new Date(busInfo.dest_arr_dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                    )}
                </div>

                <div className="bb-info-tabs">
                    {[
                        { id: 'info',     label: 'Bus Info' },
                        { id: 'cancel',   label: 'Cancellation' },
                        { id: 'boarding', label: 'Boarding' },
                        { id: 'dropping', label: 'Dropping' },
                    ].map(t => (
                        <button
                            key={t.id}
                            className={`bb-info-tab ${infoTab === t.id ? 'active' : ''}`}
                            onClick={() => setInfoTab(t.id)}
                        >{t.label}</button>
                    ))}
                </div>

                <div className="bb-info-body">
                    {infoTab === 'info' && (
                        <div className="bb-bus-info">
                            {amenityList.length > 0 && (
                                <>
                                    <div className="bb-info-section-title">Amenities</div>
                                    <div className="bb-amenities-grid">
                                        {amenityList.map(a => (
                                            <span key={a} className="bb-amenity-pill">{a}</span>
                                        ))}
                                    </div>
                                </>
                            )}
                            {busInfo?.duration_mins && (
                                <div className="bb-info-row">
                                    <Clock size={14} />
                                    <span>Duration: {Math.floor(busInfo.duration_mins / 60)}h {busInfo.duration_mins % 60}m</span>
                                </div>
                            )}
                            {busInfo?.m_ticket_enabled && (
                                <div className="bb-info-row">
                                    <Check size={14} />
                                    <span>M-Ticket accepted</span>
                                </div>
                            )}
                        </div>
                    )}
                    {infoTab === 'cancel'   && <CancellationTable policies={policies} />}
                    {infoTab === 'boarding' && <PointList points={boardingPoints} dateKey="boardingDate" />}
                    {infoTab === 'dropping' && <PointList points={droppingPoints} dateKey="droppingDate" />}
                </div>
            </div>

            {/* Footer CTA */}
            <div className="bb-step-footer">
                <div className="bb-footer-summary">
                    {hasSelection ? (
                        <>
                            <span className="bb-footer-count">
                                {selectedSeats.length} seat{selectedSeats.length > 1 ? 's' : ''}
                            </span>
                            <span className="bb-footer-fare">₹{totalFare.toLocaleString('en-IN')}</span>
                            <span className="bb-footer-tax">
                                +₹{selectedSeats.reduce((s, x) => s + x.serviceTaxAmount, 0).toLocaleString('en-IN')} GST
                            </span>
                        </>
                    ) : (
                        <span className="bb-footer-hint">Select at least one seat</span>
                    )}
                </div>
                <button
                    className={`bb-cta-btn ${!hasSelection ? 'bb-cta-btn--disabled' : ''}`}
                    disabled={!hasSelection}
                    onClick={onNext}
                >
                    Select boarding &amp; dropping points
                </button>
            </div>
        </div>
    );
}

// ─── Step 2: Boarding / Dropping point selection ──────────────────────
function StepPoints({ layoutData, selectedBoarding, selectedDropping, onSelectBoarding, onSelectDropping, onNext }) {
    const boardingPoints = layoutData?.boardingPoints || [];
    const droppingPoints = layoutData?.droppingPoints || [];
    const canProceed     = selectedBoarding && selectedDropping;

    const cleanName = (name) => name?.replace(/^cityflo:\s+/, '').trim() || name;

    const PointOptionList = ({ points, selected, onSelect, dateKey }) => (
        <div className="bb-points-list">
            {points.map(pt => {
                const isSelected = selected?.id === pt.id;
                return (
                    <button
                        key={pt.id}
                        className={`bb-point-option ${isSelected ? 'bb-point-option--selected' : ''}`}
                        onClick={() => onSelect(pt)}
                    >
                        <div className="bb-point-option-time">
                            <span className="bb-point-option-hm">{pt.time?.replace(':00 ', ' ')}</span>
                            {pt[dateKey] && (
                                <span className="bb-point-option-date">
                                    {new Date(pt[dateKey]).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                </span>
                            )}
                        </div>
                        <div className="bb-point-option-info">
                            <div className="bb-point-option-name">{cleanName(pt.pointName)}</div>
                            {(pt.landmark || pt.address) && (
                                <div className="bb-point-option-addr">{pt.landmark || pt.address}</div>
                            )}
                        </div>
                        <div className="bb-point-option-radio">
                            <div className={`bb-radio ${isSelected ? 'bb-radio--checked' : ''}`}>
                                {isSelected && <div className="bb-radio-dot" />}
                            </div>
                        </div>
                    </button>
                );
            })}
        </div>
    );

    return (
        <div className="bb-step-points">
            <div className="bb-points-cols">
                <div className="bb-points-col">
                    <div className="bb-points-col-header">
                        <div className="bb-points-col-title">Boarding point</div>
                        <div className="bb-points-col-sub">Select one boarding point</div>
                    </div>
                    <PointOptionList
                        points={boardingPoints}
                        selected={selectedBoarding}
                        onSelect={onSelectBoarding}
                        dateKey="boardingDate"
                    />
                </div>

                <div className="bb-points-col">
                    <div className="bb-points-col-header">
                        <div className="bb-points-col-title">Dropping point</div>
                        <div className="bb-points-col-sub">Select one dropping point</div>
                    </div>
                    <PointOptionList
                        points={droppingPoints}
                        selected={selectedDropping}
                        onSelect={onSelectDropping}
                        dateKey="droppingDate"
                    />
                </div>
            </div>

            <div className="bb-step-footer">
                <div className="bb-footer-summary">
                    {selectedBoarding ? (
                        <span className="bb-footer-hint">
                            <MapPin size={13} /> {cleanName(selectedBoarding.pointName)}
                            {selectedDropping && <> → {cleanName(selectedDropping.pointName)}</>}
                        </span>
                    ) : (
                        <span className="bb-footer-hint">Select boarding &amp; dropping points</span>
                    )}
                </div>
                <button
                    className={`bb-cta-btn ${!canProceed ? 'bb-cta-btn--disabled' : ''}`}
                    disabled={!canProceed}
                    onClick={onNext}
                >
                    Continue to passenger details
                </button>
            </div>
        </div>
    );
}

// ─── Step 3: Passenger Info ───────────────────────────────────────────
function PassengerForm({ seat, idx, value, onChange }) {
    const isLadies     = seat.ladiesSeat;
    const isMales      = seat.malesSeat;
    const genderLocked = isLadies || isMales;
    const lockedGender = isLadies ? 'Female' : 'Male';

    return (
        <div className="bb-pax-card">
            <div className="bb-pax-card-header">
                <div className="bb-pax-avatar"><User size={18} /></div>
                <div>
                    <div className="bb-pax-title">Passenger {idx + 1}</div>
                    <div className="bb-pax-subtitle">
                        Seat {seat.id} · {seat.zIndex === 0 ? 'Lower' : 'Upper'} deck
                        {genderLocked && (
                            <span className="bb-gender-badge">{lockedGender} seat</span>
                        )}
                    </div>
                </div>
            </div>
            <div className="bb-pax-fields">
                <label className="bb-field">
                    <span>Full name *</span>
                    <input
                        type="text"
                        placeholder="As on ID proof"
                        value={value.name || ''}
                        onChange={e => onChange({ ...value, name: e.target.value })}
                    />
                </label>
                <label className="bb-field">
                    <span>Age *</span>
                    <input
                        type="number"
                        min="1" max="99"
                        placeholder="Years"
                        value={value.age || ''}
                        onChange={e => onChange({ ...value, age: e.target.value })}
                    />
                </label>
                <div className="bb-field">
                    <span>Gender *</span>
                    {genderLocked ? (
                        <div className="bb-gender-locked">
                            {lockedGender} <span>(reserved seat)</span>
                        </div>
                    ) : (
                        <div className="bb-gender-pills">
                            {['Male', 'Female', 'Other'].map(g => (
                                <button
                                    key={g}
                                    type="button"
                                    className={`bb-gender-pill ${value.gender === g ? 'active' : ''}`}
                                    onClick={() => onChange({ ...value, gender: g })}
                                >{g}</button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function StepPassenger({ selectedSeats, selectedBoarding, selectedDropping, busInfo, rcBookingId, validPromoCodes, pricing, paymentLoading, onSubmit }) {
    // Pre-fill email from Firebase auth if logged in
    const _authEmail = (() => {
        try {
            const { getAuth } = require('firebase/auth');
            return getAuth().currentUser?.email || '';
        } catch { return ''; }
    })();
    const [contact, setContact] = useState({ phone: '', email: _authEmail, whatsapp: true });
    const [passengers, setPassengers] = useState(
        selectedSeats.map(s => ({
            seatId: s.id,
            name:   '',
            age:    '',
            gender: s.ladiesSeat ? 'Female' : s.malesSeat ? 'Male' : '',
        }))
    );

    // Promo + pricing state. Keep this calculation identical to the backend
    // calc_bus_total() contract: promo discounts the RouteChef platform fee.
    const [appliedPromo, setAppliedPromo] = useState(null); // { code, amount, description }

    const operatorFare = selectedSeats.reduce((sum, seat) => sum + Number(seat.fare || 0), 0);
    const operatorTax  = selectedSeats.reduce((sum, seat) => sum + Number(seat.serviceTaxAmount || 0), 0);
    const baseFare     = selectedSeats.reduce(
        (sum, seat) => sum + Number(
            seat.totalFareWithTaxes != null
                ? seat.totalFareWithTaxes
                : Number(seat.fare || 0) + Number(seat.serviceTaxAmount || 0)
        ),
        0
    );

    const platformFee = Number(pricing?.platformFee || 0);
    const gstRate     = Number(pricing?.gstRate || 0);
    const promoDiscount = Math.min(
        Math.max(0, Number(appliedPromo?.amount || 0)),
        Math.max(0, platformFee)
    );
    const effectivePlatformFee = Math.max(0, platformFee - promoDiscount);
    const gstOnPlatformFee = Number((effectivePlatformFee * gstRate / 100).toFixed(2));
    const totalFare = Number((baseFare + effectivePlatformFee + gstOnPlatformFee).toFixed(2));

    const cleanName  = (name) => name?.replace(/^cityflo:\s+/, '').trim() || name;
    const isValid    = contact.phone.length >= 10 && contact.email.includes('@') &&
        passengers.every(p => p.name.trim() && p.age && p.gender);

    return (
        <div className="bb-step-pax">
            <div className="bb-pax-main">
                {/* Contact */}
                <div className="bb-pax-section">
                    <div className="bb-pax-section-title">Contact details</div>
                    <p className="bb-pax-section-sub">Ticket will be sent to this number</p>
                    <div className="bb-contact-fields">
                        <div className="bb-field bb-field--phone">
                            <span className="bb-phone-prefix">+91</span>
                            <input
                                type="tel"
                                placeholder="Mobile number *"
                                value={contact.phone}
                                maxLength={10}
                                onChange={e => setContact({ ...contact, phone: e.target.value.replace(/\D/, '') })}
                            />
                        </div>
                        <label className="bb-field">
                            <input
                                type="email"
                                placeholder="Email address (required)"
                                value={contact.email}
                                required
                                style={_authEmail && contact.email === _authEmail ? { background: '#F0FDF4', borderColor: '#86EFAC' } : {}}
                                onChange={e => setContact({ ...contact, email: e.target.value })}
                            />
                            {_authEmail && contact.email === _authEmail && (
                                <span style={{ fontSize: 12, color: '#16A34A', marginTop: 4, display: 'block' }}>
                                    ✓ From your account — you can edit if needed
                                </span>
                            )}
                        </label>
                        <label className="bb-field">
                            <input
                                type="text"
                                placeholder="Address (optional)"
                                value={contact.address || ''}
                                onChange={e => setContact({ ...contact, address: e.target.value })}
                            />
                        </label>
                        <label className="bb-whatsapp-row">
                            <div
                                className={`bb-toggle ${contact.whatsapp ? 'active' : ''}`}
                                onClick={() => setContact({ ...contact, whatsapp: !contact.whatsapp })}
                            >
                                <div className="bb-toggle-knob" />
                            </div>
                            <span>Send booking updates on WhatsApp</span>
                        </label>
                    </div>
                </div>

                {/* Passenger forms */}
                <div className="bb-pax-section">
                    <div className="bb-pax-section-title">Passenger details</div>
                    {selectedSeats.map((seat, idx) => (
                        <PassengerForm
                            key={seat.id}
                            seat={seat}
                            idx={idx}
                            value={passengers[idx]}
                            onChange={v => {
                                const next = [...passengers];
                                next[idx] = v;
                                setPassengers(next);
                            }}
                        />
                    ))}
                </div>
            </div>

            {/* Right: booking summary */}
            <div className="bb-pax-summary">
                <div className="bb-summary-card">
                    <div className="bb-summary-operator">
                        {busInfo?.travel_name || busInfo?.operatorName}
                    </div>
                    <div className="bb-summary-bustype">{busInfo?.bus_type || busInfo?.busType}</div>

                    <div className="bb-summary-journey">
                        <div className="bb-summary-point">
                            <span className="bb-summary-time">{selectedBoarding?.time?.replace(':00 ', ' ')}</span>
                            <span className="bb-summary-place">{cleanName(selectedBoarding?.pointName)}</span>
                        </div>
                        <div className="bb-summary-arrow">↓</div>
                        <div className="bb-summary-point">
                            <span className="bb-summary-time">{selectedDropping?.time?.replace(':00 ', ' ')}</span>
                            <span className="bb-summary-place">{cleanName(selectedDropping?.pointName)}</span>
                        </div>
                    </div>

                    <div className="bb-summary-seats">
                        <div className="bb-summary-seats-title">
                            {selectedSeats.length} seat{selectedSeats.length > 1 ? 's' : ''}
                        </div>
                        <div className="bb-summary-seat-tags">
                            {selectedSeats.map(s => (
                                <span key={s.id} className="bb-summary-seat-tag">
                                    {s.id} · {s.zIndex === 0 ? 'Lower' : 'Upper'}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Promo list — same apply/remove flow as flights */}
                    <BusPromoCodeList
                        validPromoCodes={validPromoCodes}
                        userToken={window.__busUserToken || null}
                        userContact={window.__busUserContact || ''}
                        rcBookingId={rcBookingId}
                        appliedPromo={appliedPromo}
                        setAppliedPromo={setAppliedPromo}
                        baseFare={baseFare}
                    />

                    <div className="bb-summary-fare-breakdown">
                        <div className="bb-summary-fare-row">
                            <span>Bus fare</span>
                            <span>₹{operatorFare.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="bb-summary-fare-row">
                            <span>Operator taxes</span>
                            <span>₹{operatorTax.toLocaleString('en-IN')}</span>
                        </div>
                        {platformFee > 0 && (
                            <div className="bb-summary-fare-row">
                                <span>RouteChef platform fee</span>
                                <span>₹{platformFee.toLocaleString('en-IN')}</span>
                            </div>
                        )}
                        {promoDiscount > 0 && (
                            <div className="bb-summary-fare-row bb-summary-fare-promo">
                                <span>Promo ({appliedPromo.code})</span>
                                <span>− ₹{promoDiscount.toLocaleString('en-IN')}</span>
                            </div>
                        )}
                        {gstOnPlatformFee > 0 && (
                            <div className="bb-summary-fare-row">
                                <span>GST on platform fee</span>
                                <span>₹{gstOnPlatformFee.toLocaleString('en-IN')}</span>
                            </div>
                        )}
                        <div className="bb-summary-fare-total">
                            <span>Total</span>
                            <span>₹{totalFare.toLocaleString('en-IN')}</span>
                        </div>
                    </div>

                    {rcBookingId && (
                        <div className="bb-summary-booking-id">
                            Ref: {rcBookingId}
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="bb-step-footer">
                <div className="bb-footer-summary">
                    <span className="bb-footer-fare">₹{totalFare.toLocaleString('en-IN')}</span>
                    <span className="bb-footer-hint">incl. taxes</span>
                </div>
                <button
                    className={`bb-cta-btn bb-cta-btn--dark ${(!isValid || paymentLoading) ? 'bb-cta-btn--disabled' : ''}`}
                    disabled={!isValid || paymentLoading}
                    onClick={() => !paymentLoading && onSubmit({
                        contact,
                        passengers,
                        baseFare,
                        finalTotalFare: totalFare,
                        appliedPromoCode: appliedPromo?.code || '',
                        appliedPromoDiscount: promoDiscount,
                    })}
                >
                    {paymentLoading ? 'Opening payment…' : 'Proceed to payment'}
                </button>
            </div>
        </div>
    );
}

// ─── Main page ────────────────────────────────────────────────────────
const STEPS = ['Select seats', 'Board/Drop point', 'Passenger Info'];

export default function BusBookingPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const [urlSearchParams] = useSearchParams();
    const { busInfo, searchParams: sp } = location.state || {};

    // busInfo comes from ResultCard → passed via navigate state.
    // When the page is opened directly from a booking short link (/b/CODE →
    // redirects to /book-bus?rid=...&src=...&dst=...&dt=...&inv=...&fb=<b64>),
    // there is no navigation state — we read URL params directly.
    // 'fb' is a base64url-encoded fallback URL appended by create_short_booking_link;
    // we decode it to extract params as a secondary fallback (primary: the plain params).
    const _ridFromUrl  = urlSearchParams.get('rid');
    const _srcFromUrl  = urlSearchParams.get('src');
    const _dstFromUrl  = urlSearchParams.get('dst');
    const _dtFromUrl   = urlSearchParams.get('dt');
    const _invFromUrl  = urlSearchParams.get('inv');

    // Decode 'fb' fallback param (base64url, no padding) if plain params are absent
    const _fbDecoded = (() => {
        const fb = urlSearchParams.get('fb');
        if (!fb || (_ridFromUrl && _srcFromUrl)) return null;  // primary params present, skip
        try {
            // restore base64 padding stripped by short_link_helper
            const padded = fb.replace(/-/g, '+').replace(/_/g, '/') + '=='.slice(0, (4 - fb.length % 4) % 4);
            const decoded = atob(padded);
            const fbParams = new URLSearchParams(decoded.includes('?') ? decoded.split('?')[1] : decoded);
            return {
                rid: fbParams.get('rid'),
                src: fbParams.get('src'),
                dst: fbParams.get('dst'),
                dt:  fbParams.get('dt'),
                inv: fbParams.get('inv'),
            };
        } catch { return null; }
    })();

    const _rid = _ridFromUrl || _fbDecoded?.rid;
    const _src = _srcFromUrl || _fbDecoded?.src;
    const _dst = _dstFromUrl || _fbDecoded?.dst;
    const _dt  = _dtFromUrl  || _fbDecoded?.dt;
    const _inv = _invFromUrl || _fbDecoded?.inv;

    const _segFromUrl = _rid ? {
        result_idx:     _rid,
        route_id:       _rid,
        origin_name:    _src || '',
        dest_name:      _dst || '',
        inventory_type: _inv || '7',
        origin_dep_dt:  _dt  ? `${_dt}T00:00:00` : null,
    } : null;

    // busInfo comes from ResultCard → passed via navigate state
    const seg = busInfo?.segment || busInfo || _segFromUrl;

    // sp (searchParams) used for date + city name fallbacks
    const spEffective = sp || (_rid ? {
        from: _src || '',
        to:   _dst || '',
        date: _dt  || '',
    } : {});

    const [step, setStep]             = useState(0);
    const [layoutData, setLayoutData] = useState(null);
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState(null);
    const [rcBookingId, setRcBookingId] = useState(null);
    const [validPromoCodes, setValidPromoCodes] = useState({});
    const [pricing, setPricing] = useState({ platformFee: 15, gstRate: 18 });

    const [selectedSeats, setSelectedSeats]       = useState([]);
    const [selectedBoarding, setSelectedBoarding] = useState(null);
    const [selectedDropping, setSelectedDropping] = useState(null);

    // Fetch layout — resolves auth first so From/user_id is always set
    useEffect(() => {
        if (!seg) { setError('Missing bus info'); setLoading(false); return; }

        const routeScheduleId = seg.result_idx || seg.route_id || seg.routeScheduleId;
        const inventoryType   = String(seg.inventory_type || seg.inventoryType || 7);
        const src  = seg.origin_name || spEffective?.from || '';
        const dest = seg.dest_name   || spEffective?.to   || '';

        // Normalise date to yyyy-mm-dd regardless of what format arrived in the URL.
        // URL params from booking links carry dd-mm-yyyy; navigate-state sp?.date
        // may be either format; seg.origin_dep_dt is an ISO datetime string.
        const _normDate = (raw) => {
            if (!raw) return null;
            // Already yyyy-mm-dd (from ISO datetime split or correctly formatted)
            if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
            // dd-mm-yyyy  →  yyyy-mm-dd
            const m = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
            if (m) return `${m[3]}-${m[2]}-${m[1]}`;
            return raw;
        };
        const doj = _normDate(spEffective?.date)
            || _normDate(seg.origin_dep_dt?.split('T')[0])
            || new Date().toISOString().split('T')[0];

        const fetchLayout = async () => {
            // Wait for auth to settle (mirrors FligthBooking.js pattern)
            let user = auth.currentUser;
            if (!user) {
                user = await new Promise(resolve => {
                    const timeout = setTimeout(() => resolve(null), 3500);
                    const unsub = auth.onAuthStateChanged(u => {
                        clearTimeout(timeout);
                        unsub();
                        resolve(u);
                    });
                });
            }

            // Resolve userContact exactly as FligthBooking.js does:
            // webbot_new:<email|phone> if logged in, localStorage fallback if not
            let userContact = '';
            let userToken   = null;
            if (user) {
                userToken   = await user.getIdToken().catch(() => null);
                userContact = user.email
                    || (user.phoneNumber ? user.phoneNumber.replace('+91', '') : '');
            }
            if (!userContact) {
                userContact = localStorage.getItem('userEmail')
                    || localStorage.getItem('email')
                    || '';
                if (!userContact) {
                    try {
                        const stored = JSON.parse(localStorage.getItem('user') || '{}');
                        userContact  = stored.email || stored.phone || stored.phoneNumber || '';
                    } catch {}
                }
            }
            // Stash for downstream calls (handlePayment reuses these)
            window.__busUserContact = userContact;
            window.__busUserToken   = userToken;

            try {
                const data = await getBusSeatLayout({
                    sourceCity:      src,
                    destinationCity: dest,
                    inventoryType,
                    routeScheduleId,
                    date:            doj,
                    userToken,
                    userContact,
                });

                if (data?.status === 200 && data?.data?.bus_seat_layout) {
                    const layout = data.data.bus_seat_layout;
                    setLayoutData(layout);

                    // Promo/pricing metadata may be returned alongside the layout
                    // or nested inside it depending on the orchestrator version.
                    setValidPromoCodes(
                        data.data.valid_promo_codes
                        || data.valid_promo_codes
                        || layout?.valid_promo_codes
                        || layout?.validPromoCodes
                        || {}
                    );
                    setPricing({
                        platformFee: Number(
                            data.data.rc_platform_fee
                            ?? data.data.platform_fee
                            ?? layout?.rc_platform_fee
                            ?? layout?.platform_fee
                            ?? 15
                        ),
                        gstRate: Number(
                            data.data.rc_gst_rate
                            ?? data.data.gst_rate
                            ?? layout?.rc_gst_rate
                            ?? layout?.gst_rate
                            ?? 18
                        ),
                    });

                    // rc_booking_id from seat layout is the canonical ID for
                    // create_order_bus and all subsequent polling — persist it
                    const rcId = data.rc_booking_id || null;
                    setRcBookingId(rcId);
                    if (rcId) {
                        localStorage.setItem('rc_bus_booking_id', rcId);

                        // Source of truth for the promo list is the booking record.
                        // This also supports older seat-layout responses that do not
                        // include valid_promo_codes directly.
                        try {
                            const promoResp = await getBusPromoCodes({
                                rcBookingId: rcId,
                                userToken,
                                userContact,
                            });
                            if (promoResp?.status === 200 && promoResp?.data?.valid_promo_codes) {
                                setValidPromoCodes(promoResp.data.valid_promo_codes);
                                setPricing({
                                    platformFee: Number(promoResp.data.rc_platform_fee ?? 15),
                                    gstRate: Number(promoResp.data.rc_gst_rate ?? 18),
                                });
                            }
                        } catch (promoErr) {
                            console.warn('[BusBookingPage] promo list fetch failed:', promoErr);
                        }
                    }
                } else {
                    setError(data?.message || 'Failed to load seat layout');
                }
            } catch (e) {
                setError(e.message || 'Network error loading seat layout');
            } finally {
                setLoading(false);
            }
        };

        fetchLayout();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleSeat = useCallback((seat) => {
        setSelectedSeats(prev => {
            const exists = prev.find(s => s.id === seat.id);
            return exists ? prev.filter(s => s.id !== seat.id) : [...prev, seat];
        });
    }, []);

    const [paymentLoading, setPaymentLoading] = useState(false);

    // ── Build blockTicket payload from collected data ────────────────────────
    // Split full name into firstName + lastName for ETS API
    // ETS doesn't meaningfully use lastName but requires the field
    const splitName = (fullName = '') => {
        const parts = fullName.trim().split(/\s+/);
        if (parts.length === 1) return { first: parts[0], last: parts[0] };
        const last  = parts[parts.length - 1];
        const first = parts.slice(0, -1).join(' ');
        return { first, last };
    };

    const buildBlockPayload = (formData) => {
        const { contact, passengers } = formData;
        const routeScheduleId = seg.result_idx || seg.route_id;
        const inventoryType   = String(seg.inventory_type || seg.inventoryType || 7);
        const src  = cleanCity(seg.origin_name || sp?.from || '');
        const dest = cleanCity(seg.dest_name   || sp?.to   || '');
        const doj  = sp?.date || (seg.origin_dep_dt ? seg.origin_dep_dt.split('T')[0] : '');

        const blockSeatPaxDetails = selectedSeats.map((seat, idx) => {
            const pax = passengers[idx] || {};
            return {
                name:                        splitName(pax.name).first,
                lastName:                    splitName(pax.name).last,
                age:                         String(pax.age || ''),
                sex:                         pax.gender === 'Female' ? 'F' : 'M',
                seatNbr:                     seat.id,
                fare:                        seat.fare,
                serviceTaxAmount:            seat.serviceTaxAmount,
                operatorServiceChargeAbsolute: seat.operatorServiceChargeAbsolute || 0,
                totalFareWithTaxes:          seat.totalFareWithTaxes,
                ladiesSeat:                  seat.ladiesSeat,
                mobile:                      contact.phone,
                email:                       pax.email || contact.email || '',
                title:                       pax.gender === 'Female' ? 'Ms' : 'Mr',
                primary:                     idx === 0,
                ac:                          seat.ac,
                sleeper:                     seat.sleeper,
            };
        });

        return {
            sourceCity:          src,
            destinationCity:     dest,
            doj:                 doj,
            routeScheduleId:     routeScheduleId,
            inventoryType:       inventoryType,
            boardingPoint: {
                id:       selectedBoarding.id,
                location: selectedBoarding.address
                       || selectedBoarding.landmark
                       || selectedBoarding.pointName
                       || selectedBoarding.name
                       || selectedBoarding.id
                       || '',
                time:     selectedBoarding.time,
            },
            droppingPoint: {
                id:       selectedDropping.id,
                location: selectedDropping.address
                       || selectedDropping.landmark
                       || selectedDropping.pointName
                       || selectedDropping.name
                       || selectedDropping.id
                       || '',
                time:     selectedDropping.time,
            },
            customerName:        splitName(passengers[0]?.name || '').first,
            customerLastName:    splitName(passengers[0]?.name || '').last,
            customerEmail:       contact.email || '',
            customerPhone:       contact.phone || '',
            emergencyPhNumber:   contact.phone || '',
            customerAddress:     contact.address
                              || selectedBoarding.address
                              || selectedBoarding.landmark
                              || selectedBoarding.pointName
                              || selectedBoarding.name
                              || '',
            blockSeatPaxDetails,
        };
    };

    // ── Main payment handler (called from StepPassenger onSubmit) ────────────
    const handlePayment = async (formData) => {
        if (paymentLoading) return;
        setPaymentLoading(true);

        try {
            // Resolve user identity for API calls
            const user        = auth.currentUser;
            const userToken   = user ? await user.getIdToken().catch(() => null) : null;
            const userContact = user?.email || user?.phoneNumber?.replace('+91', '') || '';
            const userName    = formData.passengers?.[0]?.name || '';

            // The request amount is the selected-seat fare (including operator
            // taxes). The backend adds RouteChef fees/GST and applies promo
            // server-side. This prevents a stale amount stored when the layout
            // was fetched from being used for the Razorpay order.
            const baseFare = Number(formData.baseFare || selectedSeats.reduce(
                (sum, seat) => sum + Number(
                    seat.totalFareWithTaxes != null
                        ? seat.totalFareWithTaxes
                        : Number(seat.fare || 0) + Number(seat.serviceTaxAmount || 0)
                ),
                0
            ));
            const blockPayload = buildBlockPayload(formData);

            // 1. Create Razorpay order
            const busTest = busInfo?.bus_test !== undefined
                ? Boolean(busInfo.bus_test)
                : Boolean(FEATURES.BUS_TEST);

            const orderResp = await createBusOrder({
                rcBookingId:      rcBookingId,
                totalFare:        baseFare,
                appliedPromoCode: formData.appliedPromoCode || '',
                blockPayload:     blockPayload,
                segmentInfo:      busInfo || null,
                busTest,
                userToken,
                userContact,
            });

            if (orderResp?.status !== 200 || !orderResp?.data?.rzp_order_id) {
                throw new Error(orderResp?.message || 'Failed to create payment order');
            }

            const { rzp_order_id, rzp_key_id, amount } = orderResp.data;
            const razorpayAmount = Number(amount); // backend is authoritative

            // Store booking ID for polling page
            localStorage.setItem('rc_bus_booking_id', rcBookingId);

            // 2. Open Razorpay modal
            openBusPaymentModal({
                rzpOrderId:   rzp_order_id,
                rzpKeyId:     rzp_key_id,
                amountRupees: razorpayAmount,
                rcBookingId:  rcBookingId,
                userContact,
                userName,

                onSuccess: async (rzpResponse) => {
                    // 3. Notify backend of payment success → triggers block+book
                    try {
                        await updateBusBookingStatus({
                            rcBookingId,
                            newStatus:     'PAYMENT_SUCCESS',
                            busTest,
                            paymentId:     rzpResponse.razorpay_payment_id,
                            paymentStatus: 'SUCCESS',
                            paymentMeta:   rzpResponse,
                            userToken,
                            userContact,
                        });
                    } catch (e) {
                        console.warn('[BusBookingPage] updateBusBookingStatus error (non-fatal):', e);
                    }

                    // 4. Navigate to confirmation page. Keep rc_booking_id in the URL so refresh/direct access works.
                    navigate(`/book-bus/confirmation?rc_booking_id=${encodeURIComponent(rcBookingId)}`, {
                        state: {
                            rc_booking_id: rcBookingId,
                            userContact,
                            userToken,
                            source:        'search',
                            searchQuery:   '',
                        },
                    });
                },

                onFailure: async (errMsg) => {
                    try {
                        await updateBusBookingStatus({
                            rcBookingId,
                            newStatus:     'PAYMENT_FAILED',
                            paymentStatus: 'FAILED',
                            userToken,
                            userContact,
                        });
                    } catch {}
                    alert(`Payment failed: ${errMsg}`);
                    setPaymentLoading(false);
                },

                onDismiss: () => {
                    setPaymentLoading(false);
                },
            });

        } catch (err) {
            console.error('[BusBookingPage] handlePayment error:', err);
            alert(err.message || 'Something went wrong. Please try again.');
            setPaymentLoading(false);
        }
    };

    const handleClose = () => navigate(-1);
    const stepDone    = (i) => i < step;

    return (
        <div className="bb-page">
            {/* Top bar */}
            <div className="bb-topbar">
                <button className="bb-close-btn" onClick={handleClose}>
                    <X size={20} />
                </button>
                <div className="bb-topbar-route">
                    <span>{seg?.origin_name || 'Origin'}</span>
                    <ChevronRight size={16} className="bb-topbar-arrow" />
                    <span>{seg?.dest_name || 'Destination'}</span>
                </div>
                <div className="bb-topbar-spacer" />
            </div>

            {/* Step tabs */}
            <div className="bb-stepper">
                {STEPS.map((label, i) => (
                    <button
                        key={label}
                        className={[
                            'bb-step-tab',
                            i === step      ? 'bb-step-tab--active' : '',
                            stepDone(i)     ? 'bb-step-tab--done'   : '',
                            i > step        ? 'bb-step-tab--locked' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={() => stepDone(i) && setStep(i)}
                        disabled={i > step}
                    >
                        <span className="bb-step-dot">
                            {stepDone(i) ? <Check size={11} strokeWidth={3} /> : i + 1}
                        </span>
                        {label}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="bb-content">
                {loading && (
                    <div className="bb-loading">
                        <div className="bb-spinner" />
                        <span>Loading seat layout…</span>
                    </div>
                )}
                {error && !loading && (
                    <div className="bb-error">
                        <AlertCircle size={32} />
                        <p>{error}</p>
                        <button onClick={handleClose}>Go back</button>
                    </div>
                )}
                {!loading && !error && layoutData && (
                    <>
                        {step === 0 && (
                            <StepSeats
                                layoutData={layoutData}
                                busInfo={seg}
                                selectedSeats={selectedSeats}
                                onToggleSeat={toggleSeat}
                                onNext={() => setStep(1)}
                            />
                        )}
                        {step === 1 && (
                            <StepPoints
                                layoutData={layoutData}
                                selectedBoarding={selectedBoarding}
                                selectedDropping={selectedDropping}
                                onSelectBoarding={setSelectedBoarding}
                                onSelectDropping={setSelectedDropping}
                                onNext={() => setStep(2)}
                                onBack={() => setStep(0)}
                            />
                        )}
                        {step === 2 && (
                            <StepPassenger
                                selectedSeats={selectedSeats}
                                selectedBoarding={selectedBoarding}
                                selectedDropping={selectedDropping}
                                busInfo={seg}
                                rcBookingId={rcBookingId}
                                validPromoCodes={validPromoCodes}
                                pricing={pricing}
                                paymentLoading={paymentLoading}
                                onSubmit={(formData) => handlePayment(formData)}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
